import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Footer = () => {
  const { language } = useLanguage();
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const quickLinks = [
    { name: translations.nav.about[language], path: '/about' },
    { name: translations.nav.services[language], path: '/services' },
    { name: translations.nav.news[language], path: '/news' },
    { name: translations.nav.careers[language], path: '/careers' },
    { name: translations.nav.contact[language], path: '/contact' }
  ];

  return (
    <>
      {/* Transition gradient - smooth visual bridge from content to footer */}
      <div className="h-6 md:h-0 bg-gradient-to-b from-transparent via-primary/5 to-primary/15"></div>
      
      <footer className="bg-primary text-white" data-testid="footer">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-12 lg:py-16">
          
          {/* Mobile Layout */}
          {isMobile ? (
            <div className="space-y-4">
              
              {/* 1. Brand Block - Horizontal Layout (logo left, text right) */}
              <div className="flex items-center gap-3 pb-3.5 border-b border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_chengyang-taipei/artifacts/xw286hjg_chenyangcpalogo.png"
                  alt="Logo"
                  className="h-9 w-auto object-contain flex-shrink-0"
                  style={{ mixBlendMode: 'lighten' }}
                />
                <div className="min-w-0">
                  <h3 className="text-sm font-noto-sans font-medium text-white leading-tight">
                    {language === 'zh' ? '誠揚聯合會計師事務所' : 'Cheng Yang & Co., CPAs'}
                  </h3>
                  <p className="text-sm text-white font-noto-sans font-medium mt-0.5">
                    {language === 'zh' ? '台北分所' : 'Taipei Division'}
                  </p>
                </div>
              </div>

              {/* 2. Contact Information */}
              <div className="pb-3.5 border-b border-white/10">
                <h4 className="text-xs font-noto-sans font-semibold mb-2.5 text-secondary uppercase tracking-wider">
                  {translations.footer.contactInfo[language]}
                </h4>
                
                <div className="space-y-2">
                  {/* Phone */}
                  <a 
                    href="tel:02-25421566"
                    className="flex items-center gap-2.5 py-1.5 rounded-lg active:bg-white/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
                      <Phone size={14} className="text-secondary" />
                    </div>
                    <div>
                      <p className="text-[13px] font-noto-sans font-medium text-white">
                        {language === 'zh' ? '許小姐' : 'Ms. Hsu'}
                      </p>
                      <p className="text-[13px] text-white/75 font-noto-sans">
                        02-25421566 #203
                      </p>
                    </div>
                  </a>

                  {/* Address */}
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Cheng%20Yang%20%26%20Co.%2C%20CPAs%20Taipei%20Division&query_place_id=ChIJQSpaGJSpQjQRR_KB_aOFXM8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2.5 py-1.5 rounded-lg active:bg-white/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white/75 font-noto-sans leading-snug">
                        {language === 'zh' 
                          ? '台北市中山區長安東路一段27號9樓之1'
                          : '9F-1, No. 27, Sec. 1, Chang\'an E. Rd., Taipei'
                        }
                      </p>
                      <span className="text-[10px] text-secondary flex items-center gap-1 mt-1.5 font-medium">
                        <ExternalLink size={9} />
                        {language === 'zh' ? '查看地圖' : 'Open in Maps'}
                      </span>
                    </div>
                  </a>
                </div>
              </div>

              {/* 3. Business Hours */}
              <div className="pb-3.5 border-b border-white/10">
                <h4 className="text-xs font-noto-sans font-semibold mb-2 text-secondary uppercase tracking-wider">
                  {translations.footer.businessHours[language]}
                </h4>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
                    <Clock size={14} className="text-secondary" />
                  </div>
                  <div className="text-[13px] font-noto-sans">
                    <p className="text-white/75">{language === 'zh' ? '週一至週五' : 'Mon – Fri'}</p>
                    <p className="text-white font-medium">09:00 – 18:00</p>
                  </div>
                </div>
              </div>

              {/* 4. Quick Links - 2 Column Grid */}
              <div className="pb-2">
                <h4 className="text-xs font-noto-sans font-semibold mb-2 text-secondary uppercase tracking-wider">
                  {translations.footer.quickLinks[language]}
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {quickLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="text-[13px] text-white/75 hover:text-secondary active:text-secondary transition-colors font-noto-sans py-0.5"
                      data-testid={`footer-link-${link.path.slice(1)}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* 5. Copyright */}
              <div className="pt-2.5 border-t border-white/10 text-center">
                <p className="text-[11px] text-white/45 font-noto-sans leading-relaxed">
                  © {new Date().getFullYear()} {language === 'zh' ? '誠揚聯合會計師事務所 台北分所' : 'Cheng Yang & Co., CPAs'}
                  <br className={language === 'en' ? 'block' : 'hidden'} />
                  <span className={language === 'zh' ? 'mx-1.5' : 'hidden'}>·</span>
                  <span className={language === 'en' ? '' : ''}>
                    {language === 'zh' ? '版權所有' : 'Taipei Division · All Rights Reserved'}
                  </span>
                </p>
              </div>
            </div>
          ) : (
            /* Desktop Layout - Original 4-column grid */
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
                
                {/* Company Info */}
                <div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_chengyang-taipei/artifacts/xw286hjg_chenyangcpalogo.png"
                    alt="Logo"
                    className="h-12 w-auto object-contain mb-5"
                    style={{ mixBlendMode: 'lighten' }}
                  />
                  <h3 className="text-lg font-noto-sans font-medium mb-1">
                    {language === 'zh' ? '誠揚聯合會計師事務所' : 'Cheng Yang & Co., CPAs'}
                  </h3>
                  <p className="text-lg text-white font-noto-sans font-medium">
                    {language === 'zh' ? '台北分所' : 'Taipei Division'}
                  </p>
                </div>

                {/* Quick Links */}
                <div>
                  <h4 className="text-base font-noto-sans font-semibold mb-5 text-secondary">
                    {translations.footer.quickLinks[language]}
                  </h4>
                  <ul className="space-y-3">
                    {quickLinks.map((link) => (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          className="text-sm text-white/80 hover:text-secondary transition-colors font-noto-sans"
                          data-testid={`footer-link-${link.path.slice(1)}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact Info */}
                <div>
                  <h4 className="text-base font-noto-sans font-semibold mb-6 text-secondary">
                    {translations.footer.contactInfo[language]}
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Contact Person */}
                    <a 
                      href="tel:02-25421566"
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                        <Phone size={14} className="text-secondary" />
                      </div>
                      <div>
                        <p className="text-sm font-noto-sans font-medium text-white">
                          {language === 'zh' ? '許小姐' : 'Ms. Hsu'}
                        </p>
                        <p className="text-sm text-white/70 group-hover:text-secondary transition-colors font-noto-sans">
                          02-25421566 #203
                        </p>
                      </div>
                    </a>

                    {/* Address */}
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Cheng%20Yang%20%26%20Co.%2C%20CPAs%20Taipei%20Division&query_place_id=ChIJQSpaGJSpQjQRR_KB_aOFXM8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                        <MapPin size={14} className="text-secondary" />
                      </div>
                      <div className="text-sm text-white/80 font-noto-sans group-hover:text-secondary transition-colors leading-snug">
                        {language === 'zh' ? (
                          <>台北市中山區長安東路一段27號9樓之1</>
                        ) : (
                          <>9F-1, No. 27, Sec. 1, Chang'an E. Rd., Taipei</>
                        )}
                      </div>
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div>
                  <h4 className="text-base font-noto-sans font-semibold mb-6 text-secondary">
                    {translations.footer.businessHours[language]}
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={14} className="text-secondary" />
                    </div>
                    <div className="text-sm font-noto-sans">
                      <p className="text-white/80">{language === 'zh' ? '週一至週五' : 'Monday – Friday'}</p>
                      <p className="text-white font-medium mt-0.5">09:00 – 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Copyright - Desktop */}
              <div className="mt-12 pt-8 border-t border-white/10 text-center">
                <p className="text-sm text-white/60 font-noto-sans">
                  © {new Date().getFullYear()} {translations.footer.copyright[language]}
                </p>
              </div>
            </>
          )}
        </div>
      </footer>
    </>
  );
};

export default Footer;
